import React from 'react';
import { motion } from 'framer-motion';

interface LoadingSpinnerProps {
  text?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface PageLoaderProps {
  pageName: string;
  subtitle?: string;
  loadingText?: string;
}

/**
 * Small loading spinner for inline use (e.g., inside modals, search results, buttons)
 * 
 * @example
 * // Inside search results
 * <LoadingSpinner text="Searching players..." size="sm" />
 * 
 * // In a button
 * <LoadingSpinner size="sm" />
 * 
 * // For saving states
 * <LoadingSpinner text="Saving roster..." size="sm" />
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  text = "Loading...", 
  size = 'md' 
}) => {
  const sizeClasses = {
    sm: 'text-2xl',
    md: 'text-4xl',
    lg: 'text-6xl'
  };

  const containerClasses = {
    sm: 'p-4',
    md: 'p-8',
    lg: 'p-12'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${containerClasses[size]}`}>
      <div className="flex items-center space-x-4">
        {/* Spinning Football */}
        <div className={`${sizeClasses[size]} animate-spin`} style={{ animationDuration: '2s' }}>
          🏈
        </div>
        {/* Pinata Emoji - rotating in opposite direction */}
        <div 
          className={`${sizeClasses[size]} animate-spin`} 
          style={{ animationDuration: '3s', animationDirection: 'reverse' }}
        >
          🪅
        </div>
      </div>
      {text && (
        <p className="mt-4 text-gray-400 text-center font-medium">
          {text}
        </p>
      )}
    </div>
  );
};

/**
 * Full page loader for consistent page loading states
 * Use this when the entire page is loading, provides unified experience across all pages
 * 
 * @example
 * // Basic usage
 * if (loading) return <PageLoader pageName="Player Rankings" />
 * 
 * // With subtitle and custom loading text
 * if (loading) return (
 *   <PageLoader 
 *     pageName="Trade Calculator"
 *     subtitle="Evaluate your trades to see if you're getting fair value"
 *     loadingText="Loading player data for trade analysis..."
 *   />
 * )
 */
export const PageLoader: React.FC<PageLoaderProps> = ({ 
  pageName, 
  subtitle,
  loadingText = `Loading ${pageName.toLowerCase()}...`
}) => {
  return (
    <div className="pt-20 bg-black min-h-screen">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-4 text-fiesta-yellow"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {pageName}
          </motion.h1>
          
          {subtitle && (
            <motion.p 
              className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {subtitle}
            </motion.p>
          )}
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex flex-col items-center justify-center py-12">
              <div className="flex items-center space-x-6 mb-6">
                {/* Spinning Football */}
                <motion.div 
                  className="text-6xl"
                  animate={{ rotate: 360 }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    ease: "linear" 
                  }}
                >
                  🏈
                </motion.div>
                
                {/* Pinata Emoji - rotating in opposite direction */}
                <motion.div 
                  className="text-6xl"
                  animate={{ rotate: -360 }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "linear" 
                  }}
                >
                  🪅
                </motion.div>
              </div>
              
              <motion.p 
                className="text-xl text-gray-300 font-medium"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              >
                {loadingText}
              </motion.p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}; 